import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, GripVertical, ArrowLeft, Copy, RefreshCw,
         Eye, Save, Send, Archive, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { FIELD_TYPES, OPERATORS_FOR_TYPE, EFFECT_LABELS, newId } from "./_formMeta";

export default function FormBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user.role === "Admin" || user.role === "Manager";

  const [form, setForm] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [tab, setTab] = useState("fields");
  const [executives, setExecutives] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/forms/${id}`);
      setForm(r.data);
      setDefinition(r.data.definition);
      setDirty(false);
    } catch (e) {
      toast.error("Could not load form");
      navigate("/forms");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (canManage) api.get("/forms-executives").then((r) => setExecutives(r.data || [])).catch(() => {});
  }, [canManage]);

  // ----- Mutators (do NOT call setDirty inside if loading) -----
  const updateDef = (mutator) => {
    setDefinition((d) => {
      const next = JSON.parse(JSON.stringify(d));
      mutator(next);
      return next;
    });
    setDirty(true);
  };

  const addField = (type) => {
    updateDef((d) => {
      const sec = d.sections[0] || (d.sections[0] = { id: "s_main", title: "Main", description: null, fields: [] });
      const f = {
        id: newId("f"),
        key: `field_${sec.fields.length + 1}`,
        label: `New ${type.replace("_", " ")} field`,
        type, required: false, placeholder: "", help_text: "",
        options: (type === "select" || type === "multi_select")
          ? [{ label: "Option 1", value: "option_1" }] : [],
      };
      sec.fields.push(f);
      setSelectedFieldId(f.id);
    });
  };

  const removeField = (fid) => {
    updateDef((d) => {
      for (const sec of d.sections) sec.fields = sec.fields.filter((f) => f.id !== fid);
      // Drop rules that reference it
      d.rules = (d.rules || []).filter((r) =>
        r.source_field_id !== fid && !(r.target_field_ids || []).includes(fid));
    });
    if (selectedFieldId === fid) setSelectedFieldId(null);
  };

  const moveField = (fid, dir) => {
    updateDef((d) => {
      const sec = d.sections.find((s) => s.fields.some((f) => f.id === fid));
      if (!sec) return;
      const i = sec.fields.findIndex((f) => f.id === fid);
      const j = i + dir;
      if (j < 0 || j >= sec.fields.length) return;
      [sec.fields[i], sec.fields[j]] = [sec.fields[j], sec.fields[i]];
    });
  };

  const updateField = (fid, patch) => {
    updateDef((d) => {
      for (const sec of d.sections) for (const f of sec.fields)
        if (f.id === fid) Object.assign(f, patch);
    });
  };

  const addOption = (fid) => updateDef((d) => {
    for (const sec of d.sections) for (const f of sec.fields)
      if (f.id === fid) {
        const n = f.options.length + 1;
        f.options.push({ label: `Option ${n}`, value: `option_${n}` });
      }
  });
  const updateOption = (fid, idx, patch) => updateDef((d) => {
    for (const sec of d.sections) for (const f of sec.fields)
      if (f.id === fid) Object.assign(f.options[idx], patch);
  });
  const removeOption = (fid, idx) => updateDef((d) => {
    for (const sec of d.sections) for (const f of sec.fields)
      if (f.id === fid) f.options.splice(idx, 1);
  });

  const addRule = () => {
    const allFields = [];
    for (const sec of definition.sections) for (const f of sec.fields) allFields.push(f);
    if (allFields.length < 2) { toast.error("Add at least 2 fields first"); return; }
    updateDef((d) => {
      d.rules = d.rules || [];
      d.rules.push({
        id: newId("r"),
        source_field_id: allFields[0].id,
        operator: "equals", value: "",
        effect: "hide",
        target_field_ids: [allFields[1].id],
      });
    });
  };
  const updateRule = (rid, patch) => updateDef((d) => {
    for (const r of d.rules || []) if (r.id === rid) Object.assign(r, patch);
  });
  const removeRule = (rid) => updateDef((d) => {
    d.rules = (d.rules || []).filter((r) => r.id !== rid);
  });

  // ----- Save / publish / share -----
  const save = async () => {
    try {
      const r = await api.patch(`/forms/${id}`, {
        title: form.title, description: form.description, definition,
      });
      setForm(r.data); setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const publish = async () => {
    if (dirty) await save();
    try {
      const r = await api.post(`/forms/${id}/publish`);
      setForm(r.data);
      toast.success("Published — share URL is live");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    }
  };
  const unpublish = async () => {
    try { const r = await api.post(`/forms/${id}/unpublish`); setForm({ ...form, ...r.data }); toast.success("Reverted to draft"); }
    catch { toast.error("Could not unpublish"); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this form? Public access will be revoked.")) return;
    try { await api.post(`/forms/${id}/archive`); toast.success("Archived"); navigate("/forms"); }
    catch { toast.error("Archive failed"); }
  };
  const regenerateLink = async () => {
    if (!window.confirm("Regenerate the share link? The current public URL will stop working.")) return;
    try {
      const r = await api.post(`/forms/${id}/share-link`);
      setForm({ ...form, share_token: r.data.token });
      toast.success("New link generated");
    } catch { toast.error("Could not regenerate"); }
  };

  // ----- Visibility settings -----
  const toggleExecutive = async (uid, on) => {
    const next = on
      ? [...(form.executive_user_ids || []), uid]
      : (form.executive_user_ids || []).filter((x) => x !== uid);
    try {
      const r = await api.patch(`/forms/${id}`, { executive_user_ids: next });
      setForm({ ...form, ...r.data });
    } catch { toast.error("Failed to update"); }
  };
  const toggleVisibleToExec = async (on) => {
    try {
      const r = await api.patch(`/forms/${id}`, { visible_to_executives: on });
      setForm({ ...form, ...r.data });
    } catch { toast.error("Failed"); }
  };

  const publicUrl = useMemo(() =>
    form?.share_token ? `${window.location.origin}/f/${form.share_token}` : null,
    [form?.share_token]);

  if (!form || !definition) {
    return <div className="p-10 text-center text-sm text-slate-400">Loading…</div>;
  }
  const allFields = [];
  for (const sec of definition.sections) for (const f of sec.fields) allFields.push(f);
  const selected = selectedFieldId
    ? allFields.find((f) => f.id === selectedFieldId) : null;

  return (
    <div data-testid="form-builder-page">
      <PageHeader
        title={
          <input
            value={form.title}
            onChange={(e) => { setForm({ ...form, title: e.target.value }); setDirty(true); }}
            disabled={!canManage}
            className="bg-transparent border-none outline-none focus:bg-slate-50 px-1 -mx-1 rounded-sm w-full"
            data-testid="form-title-input"
          />
        }
        subtitle={
          <Link to="/forms" className="hover:underline inline-flex items-center gap-1">
            <ArrowLeft size={11} /> Back to forms
          </Link>
        }
        action={canManage && (
          <div className="flex items-center gap-2">
            {dirty && <span className="text-[10px] text-amber-600 font-semibold">UNSAVED</span>}
            <Button variant="outline" size="sm" onClick={save} disabled={!dirty} data-testid="btn-save">
              <Save size={13} className="mr-1.5" /> Save
            </Button>
            {form.status === "draft" && (
              <Button size="sm" onClick={publish}
                      className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-publish">
                <Send size={13} className="mr-1.5" /> Publish
              </Button>
            )}
            {form.status === "published" && (
              <Button size="sm" variant="outline" onClick={unpublish} data-testid="btn-unpublish">
                Unpublish
              </Button>
            )}
            {form.status !== "archived" && (
              <Button size="sm" variant="outline" onClick={archive} data-testid="btn-archive"
                      className="text-amber-700 hover:bg-amber-50">
                <Archive size={13} className="mr-1.5" /> Archive
              </Button>
            )}
          </div>
        )}
      >
        <div className="flex items-center gap-3 mt-1.5">
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm font-semibold ${
            form.status === "published" ? "bg-emerald-100 text-emerald-800"
              : form.status === "archived" ? "bg-amber-100 text-amber-800"
              : "bg-slate-100 text-slate-700"
          }`}>{form.status}</span>
          {publicUrl && (
            <div className="text-xs flex items-center gap-2 text-slate-600">
              <code className="bg-slate-100 px-2 py-0.5 rounded-sm font-mono">{publicUrl}</code>
              <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }}
                      className="text-slate-400 hover:text-[#0a2540]" data-testid="btn-copy-share-url">
                <Copy size={11} />
              </button>
              <a href={publicUrl} target="_blank" rel="noreferrer"
                 className="text-slate-400 hover:text-[#0a2540]" title="Open">
                <ExternalLink size={11} />
              </a>
              {canManage && (
                <button onClick={regenerateLink} title="Regenerate"
                        className="text-slate-400 hover:text-[#0a2540]" data-testid="btn-regen-link">
                  <RefreshCw size={11} />
                </button>
              )}
            </div>
          )}
        </div>
      </PageHeader>

      <div className="px-4 sm:px-8 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="fields" data-testid="tab-fields">Fields</TabsTrigger>
            <TabsTrigger value="logic" data-testid="tab-logic">Conditional Logic</TabsTrigger>
            <TabsTrigger value="visibility" data-testid="tab-visibility">Executive Access</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          {/* FIELDS */}
          <TabsContent value="fields" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
              {/* Field list */}
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                {allFields.length === 0 ? (
                  <div className="text-center py-12 text-sm text-slate-400">No fields yet. Add one from the panel →</div>
                ) : (
                  <ul className="space-y-1.5">
                    {allFields.map((f, idx) => (
                      <li key={f.id}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border ${
                            selectedFieldId === f.id ? "border-[#0a2540] bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}
                          onClick={() => setSelectedFieldId(f.id)}
                          data-testid={`field-row-${f.id}`}>
                        <GripVertical size={12} className="text-slate-300" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">
                            {f.label} {f.required && <span className="text-red-500">*</span>}
                          </div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            {f.type.replace("_", " ")} · key: <code>{f.key}</code>
                          </div>
                        </div>
                        {canManage && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); moveField(f.id, -1); }}
                                    disabled={idx === 0}
                                    className="text-slate-300 hover:text-slate-600 disabled:opacity-30">↑</button>
                            <button onClick={(e) => { e.stopPropagation(); moveField(f.id, 1); }}
                                    disabled={idx === allFields.length - 1}
                                    className="text-slate-300 hover:text-slate-600 disabled:opacity-30">↓</button>
                            <button onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                                    className="text-slate-300 hover:text-red-600"
                                    data-testid={`btn-remove-field-${f.id}`}>
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Right column: add field + field config */}
              <div className="space-y-4">
                {canManage && (
                  <div className="bg-white border border-slate-200 rounded-sm p-4">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-3">Add field</div>
                    <div className="grid grid-cols-2 gap-2">
                      {FIELD_TYPES.map((t) => (
                        <button key={t.value}
                                onClick={() => addField(t.value)}
                                className="text-left p-2.5 border border-slate-200 rounded-sm hover:border-[#0a2540] hover:bg-slate-50"
                                data-testid={`add-field-${t.value}`}>
                          <div className="text-xs font-semibold">{t.label}</div>
                          <div className="text-[10px] text-slate-400">{t.help}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selected && canManage && (
                  <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="field-config">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Field settings</div>
                    <div>
                      <Label className="text-xs">Label</Label>
                      <Input value={selected.label}
                             onChange={(e) => updateField(selected.id, { label: e.target.value })}
                             data-testid="field-label-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Key (used in submissions)</Label>
                      <Input value={selected.key}
                             onChange={(e) => updateField(selected.id, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_") })}
                             data-testid="field-key-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Placeholder</Label>
                      <Input value={selected.placeholder || ""}
                             onChange={(e) => updateField(selected.id, { placeholder: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Help text</Label>
                      <Input value={selected.help_text || ""}
                             onChange={(e) => updateField(selected.id, { help_text: e.target.value })} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Required</Label>
                      <Switch checked={selected.required}
                              onCheckedChange={(v) => updateField(selected.id, { required: v })}
                              data-testid="field-required-switch" />
                    </div>
                    {(selected.type === "select" || selected.type === "multi_select") && (
                      <div className="border-t border-slate-200 pt-3">
                        <Label className="text-xs mb-1.5 block">Options</Label>
                        {selected.options.map((opt, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 mb-1.5">
                            <Input value={opt.label}
                                   onChange={(e) => updateOption(selected.id, idx, { label: e.target.value, value: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                                   placeholder="Option label" className="text-xs" />
                            <button onClick={() => removeOption(selected.id, idx)}
                                    className="text-slate-300 hover:text-red-600">
                              <Trash2 size={11} />
                            </button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => addOption(selected.id)}
                                className="w-full mt-1">
                          <Plus size={11} className="mr-1" /> Add option
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* CONDITIONAL LOGIC */}
          <TabsContent value="logic" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm font-semibold">Conditional Logic</div>
                  <div className="text-xs text-slate-500">Rules are evaluated top-to-bottom. Hidden fields are never required and are dropped from submissions.</div>
                </div>
                {canManage && (
                  <Button size="sm" onClick={addRule} data-testid="btn-add-rule">
                    <Plus size={12} className="mr-1.5" /> Add rule
                  </Button>
                )}
              </div>
              {(definition.rules || []).length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400">No rules yet. Click the Add rule button to define one.</div>
              ) : (
                <ul className="space-y-3">
                  {definition.rules.map((r) => {
                    const src = allFields.find((f) => f.id === r.source_field_id);
                    const ops = src ? (OPERATORS_FOR_TYPE[src.type] || []) : [];
                    return (
                      <li key={r.id} className="border border-slate-200 rounded-sm p-3" data-testid={`rule-${r.id}`}>
                        <div className="flex items-start gap-2 flex-wrap text-xs">
                          <span className="text-slate-500 pt-1.5">When</span>
                          <Select value={r.source_field_id}
                                  onValueChange={(v) => updateRule(r.id, { source_field_id: v })}
                                  disabled={!canManage}>
                            <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {allFields.map((f) =>
                                <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={r.operator}
                                  onValueChange={(v) => updateRule(r.id, { operator: v })}
                                  disabled={!canManage}>
                            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ops.map((o) => <SelectItem key={o} value={o}>{o.replace("_", " ")}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {!["empty", "not_empty", "is_true", "is_false"].includes(r.operator) && (
                            <Input value={r.value || ""}
                                   onChange={(e) => updateRule(r.id, { value: e.target.value })}
                                   placeholder="value" className="w-36 h-8 text-xs"
                                   disabled={!canManage} />
                          )}
                          <span className="text-slate-500 pt-1.5">then</span>
                          <Select value={r.effect}
                                  onValueChange={(v) => updateRule(r.id, { effect: v })}
                                  disabled={!canManage}>
                            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(EFFECT_LABELS).map(([k, v]) =>
                                <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={r.target_field_ids[0] || ""}
                                  onValueChange={(v) => updateRule(r.id, { target_field_ids: [v] })}
                                  disabled={!canManage}>
                            <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {allFields.filter((f) => f.id !== r.source_field_id).map((f) =>
                                <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {canManage && (
                            <button onClick={() => removeRule(r.id)}
                                    className="text-slate-300 hover:text-red-600 pt-2 ml-auto"
                                    data-testid={`btn-remove-rule-${r.id}`}>
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* VISIBILITY */}
          <TabsContent value="visibility" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4 max-w-2xl">
              <div className="text-sm font-semibold mb-1">Executive access</div>
              <div className="text-xs text-slate-500 mb-4">
                Hidden from all executives by default. Toggle on and pick specific executives below.
                Admin and Manager always have full access.
              </div>
              <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
                <Label className="text-xs">Visible to executives</Label>
                <Switch checked={!!form.visible_to_executives}
                        onCheckedChange={toggleVisibleToExec}
                        disabled={!canManage}
                        data-testid="visible-to-exec-switch" />
              </div>
              {form.visible_to_executives && (
                <ul className="space-y-1.5">
                  {executives.length === 0 ? (
                    <li className="text-xs text-slate-400 italic">No active executives.</li>
                  ) : executives.map((e) => {
                    const on = (form.executive_user_ids || []).includes(e.user_id);
                    return (
                      <li key={e.user_id}
                          className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2">
                        <div>
                          <div className="text-sm">{e.name}</div>
                          <div className="text-[10px] text-slate-400">{e.email}</div>
                        </div>
                        <Switch checked={on}
                                onCheckedChange={(v) => toggleExecutive(e.user_id, v)}
                                disabled={!canManage}
                                data-testid={`exec-toggle-${e.user_id}`} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4 max-w-2xl space-y-4">
              <div>
                <Label className="text-xs">Description (shown on the public form)</Label>
                <Textarea value={form.description || ""}
                          onChange={(e) => { setForm({ ...form, description: e.target.value }); setDirty(true); }}
                          rows={3} disabled={!canManage} />
              </div>
              <div>
                <Label className="text-xs">Submit button label</Label>
                <Input value={definition.submit_label || "Submit"}
                       onChange={(e) => updateDef((d) => { d.submit_label = e.target.value; })}
                       disabled={!canManage} />
              </div>
              <div>
                <Label className="text-xs">Success message</Label>
                <Textarea value={definition.success_message || ""}
                          onChange={(e) => updateDef((d) => { d.success_message = e.target.value; })}
                          rows={2} disabled={!canManage} />
              </div>
              <div className="border-t border-slate-200 pt-3 flex gap-3">
                <Link to={`/forms/${id}/submissions`}>
                  <Button variant="outline" size="sm" data-testid="link-submissions">
                    <Eye size={13} className="mr-1.5" /> View submissions
                  </Button>
                </Link>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
